import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Calculator, PoundSterling, Target, Calendar, Gift, AlertCircle, CheckCircle, HelpCircle, PieChart, Briefcase, Building2 } from 'lucide-react';
import { updatePageSEO } from '../utils/seo';

const UKPensionCalculatorPage = () => {
  const navigate = useNavigate();

  // Update SEO tags when component mounts
  useEffect(() => {
    updatePageSEO({
      title: 'UK Pension Calculator - PlanLens.ai | SIPP, Workplace & State Pension Planner',
      description: 'Free UK pension calculator. Plan your SIPP, workplace pension, and State Pension. Calculate retirement income, tax-free lump sums, and optimize your pension strategy.',
      canonical: 'https://planlens.ai/calculators/uk-pension-calculator'
    });
  }, []);

  // Calculator state
  const [age, setAge] = useState(35);
  const [retirementAge, setRetirementAge] = useState(67);
  const [statePensionAge, setStatePensionAge] = useState(67);

  // SIPP/Personal Pension
  const [currentSippValue, setCurrentSippValue] = useState(50000);
  const [monthlySippContribution, setMonthlySippContribution] = useState(500);

  // Workplace Pension
  const [currentWorkplacePension, setCurrentWorkplacePension] = useState(30000);
  const [monthlyWorkplaceEmployee, setMonthlyWorkplaceEmployee] = useState(200);
  const [monthlyWorkplaceEmployer, setMonthlyWorkplaceEmployer] = useState(150);

  // Defined Benefit Pension
  const [hasDefinedBenefit, setHasDefinedBenefit] = useState(false);
  const [definedBenefitAnnual, setDefinedBenefitAnnual] = useState(10000);

  // State Pension
  const [includeStatePension, setIncludeStatePension] = useState(true);

  // Investment assumptions
  const [expectedReturn, setExpectedReturn] = useState(5);

  // UK State Pension 2024/25 - Full new State Pension
  const FULL_STATE_PENSION_ANNUAL = 11502; // £221.20/week * 52 weeks

  // Calculate comprehensive pension projections
  const calculatePensions = () => {
    const yearsToRetirement = retirementAge - age;
    const returnRate = expectedReturn / 100;

    // ===== SIPP/Personal Pension Calculation =====
    const sippAnnualContribution = monthlySippContribution * 12;
    const sippTaxRelief = sippAnnualContribution * 0.25; // 20% relief = 25% gross up
    const sippTotalAnnualContribution = sippAnnualContribution + sippTaxRelief;

    let sippFutureValue = currentSippValue;
    let sippTotalContributed = 0;
    let sippTotalTaxRelief = 0;

    for (let year = 0; year < yearsToRetirement; year++) {
      sippFutureValue = sippFutureValue * (1 + returnRate) + sippTotalAnnualContribution;
      sippTotalContributed += sippAnnualContribution;
      sippTotalTaxRelief += sippTaxRelief;
    }

    // ===== Workplace Pension Calculation =====
    const workplaceAnnualContribution = (monthlyWorkplaceEmployee + monthlyWorkplaceEmployer) * 12;
    const workplaceTaxRelief = (monthlyWorkplaceEmployee * 12) * 0.25; // Only employee contributions get tax relief
    const workplaceTotalAnnualContribution = workplaceAnnualContribution + workplaceTaxRelief;

    let workplaceFutureValue = currentWorkplacePension;
    let workplaceTotalContributed = 0;
    let workplaceTotalTaxRelief = 0;

    for (let year = 0; year < yearsToRetirement; year++) {
      workplaceFutureValue = workplaceFutureValue * (1 + returnRate) + workplaceTotalAnnualContribution;
      workplaceTotalContributed += workplaceAnnualContribution;
      workplaceTotalTaxRelief += workplaceTaxRelief;
    }

    // ===== Total Defined Contribution Pots =====
    const totalDCPots = sippFutureValue + workplaceFutureValue;

    // Calculate 25% tax-free lump sum (from DC pensions only)
    // Maximum cap: £268,275 (Lump Sum Allowance as of 2024/25)
    const UK_LUMP_SUM_ALLOWANCE = 268275;
    const taxFreeLumpSum = Math.min(totalDCPots * 0.25, UK_LUMP_SUM_ALLOWANCE);
    const remainingDCPension = totalDCPots - taxFreeLumpSum;

    // Estimate annual drawdown from DC pensions (4% rule)
    const dcAnnualDrawdown = remainingDCPension * 0.04;

    // ===== Total Annual Retirement Income =====
    // Calculate income WITHOUT State Pension (for early retirement)
    let annualIncomeBeforeStatePension = dcAnnualDrawdown;

    // Add Defined Benefit pension if included
    if (hasDefinedBenefit) {
      annualIncomeBeforeStatePension += definedBenefitAnnual;
    }

    const monthlyIncomeBeforeStatePension = annualIncomeBeforeStatePension / 12;

    // Calculate income WITH State Pension (from State Pension Age onwards)
    let totalAnnualIncome = annualIncomeBeforeStatePension;

    // Add State Pension if included (only from State Pension Age)
    if (includeStatePension) {
      totalAnnualIncome += FULL_STATE_PENSION_ANNUAL;
    }

    const monthlyIncome = totalAnnualIncome / 12;

    // ===== Total Contributions & Tax Relief =====
    const totalContributed = sippTotalContributed + workplaceTotalContributed;
    const totalTaxRelief = sippTotalTaxRelief + workplaceTotalTaxRelief;
    const totalEmployerContributions = (monthlyWorkplaceEmployer * 12) * yearsToRetirement;

    return {
      // SIPP
      sippFutureValue: Math.round(sippFutureValue),
      sippTotalContributed: Math.round(sippTotalContributed),
      sippTotalTaxRelief: Math.round(sippTotalTaxRelief),

      // Workplace
      workplaceFutureValue: Math.round(workplaceFutureValue),
      workplaceTotalContributed: Math.round(workplaceTotalContributed),
      workplaceTotalTaxRelief: Math.round(workplaceTotalTaxRelief),

      // Totals
      totalDCPots: Math.round(totalDCPots),
      taxFreeLumpSum: Math.round(taxFreeLumpSum),
      remainingDCPension: Math.round(remainingDCPension),
      dcAnnualDrawdown: Math.round(dcAnnualDrawdown),

      // Income breakdown
      definedBenefitIncome: hasDefinedBenefit ? definedBenefitAnnual : 0,
      statePensionIncome: includeStatePension ? FULL_STATE_PENSION_ANNUAL : 0,
      totalAnnualIncome: Math.round(totalAnnualIncome),
      monthlyIncome: Math.round(monthlyIncome),

      // Income before State Pension Age (for early retirement)
      annualIncomeBeforeStatePension: Math.round(annualIncomeBeforeStatePension),
      monthlyIncomeBeforeStatePension: Math.round(monthlyIncomeBeforeStatePension),

      // Summary
      totalContributed: Math.round(totalContributed),
      totalTaxRelief: Math.round(totalTaxRelief),
      totalEmployerContributions: Math.round(totalEmployerContributions),
      yearsToRetirement
    };
  };

  const results = calculatePensions();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">PlanLens AI</span>
            </div>
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 font-medium transition"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center max-w-4xl mx-auto mb-12">
          <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full mb-6">
            <PoundSterling className="w-4 h-4" />
            <span className="text-sm font-semibold">Complete UK Pension Planning Tool</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            UK Pension Calculator
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> (All Types)</span>
          </h1>

          <p className="text-xl text-gray-600 mb-8">
            Plan your complete UK retirement with SIPP, Workplace Pension, Defined Benefit, and State Pension.
            Free, comprehensive, and easy to use.
          </p>
        </div>

        {/* Calculator Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Input Section */}
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center space-x-3 mb-6">
                <Calculator className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-900">Your Details</h2>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Current Age
                    </label>
                    <input
                      type="number"
                      value={age}
                      onChange={(e) => setAge(parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="18"
                      max="75"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Retirement Age
                    </label>
                    <input
                      type="number"
                      value={retirementAge}
                      onChange={(e) => setRetirementAge(parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="55"
                      max="75"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Expected Annual Return (%)
                  </label>
                  <input
                    type="number"
                    value={expectedReturn}
                    onChange={(e) => setExpectedReturn(parseFloat(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                    max="15"
                    step="0.5"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Conservative: 4-5% | Moderate: 6-7% | Aggressive: 8-10%
                  </p>
                </div>
              </div>
            </div>

            {/* SIPP/Personal Pension */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Briefcase className="w-6 h-6 text-purple-600" />
                <h3 className="text-xl font-bold text-gray-900">SIPP / Personal Pension</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Current Value (£)
                  </label>
                  <input
                    type="number"
                    value={currentSippValue}
                    onChange={(e) => setCurrentSippValue(parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    min="0"
                    step="1000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Monthly Contribution (£)
                  </label>
                  <input
                    type="number"
                    value={monthlySippContribution}
                    onChange={(e) => setMonthlySippContribution(parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    min="0"
                    step="50"
                  />
                </div>
              </div>
            </div>

            {/* Workplace Pension */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Building2 className="w-6 h-6 text-green-600" />
                <h3 className="text-xl font-bold text-gray-900">Workplace Pension</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Current Value (£)
                  </label>
                  <input
                    type="number"
                    value={currentWorkplacePension}
                    onChange={(e) => setCurrentWorkplacePension(parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    min="0"
                    step="1000"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Monthly (£)
                    </label>
                    <input
                      type="number"
                      value={monthlyWorkplaceEmployee}
                      onChange={(e) => setMonthlyWorkplaceEmployee(parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      min="0"
                      step="50"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Employer Monthly (£)
                    </label>
                    <input
                      type="number"
                      value={monthlyWorkplaceEmployer}
                      onChange={(e) => setMonthlyWorkplaceEmployer(parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      min="0"
                      step="50"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Defined Benefit Pension */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Gift className="w-6 h-6 text-amber-600" />
                  <h3 className="text-xl font-bold text-gray-900">Defined Benefit Pension</h3>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasDefinedBenefit}
                    onChange={(e) => setHasDefinedBenefit(e.target.checked)}
                    className="w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">Include</span>
                </label>
              </div>

              {hasDefinedBenefit && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Expected Annual Income at Retirement (£)
                  </label>
                  <input
                    type="number"
                    value={definedBenefitAnnual}
                    onChange={(e) => setDefinedBenefitAnnual(parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    min="0"
                    step="1000"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Check your pension statement for projected annual income
                  </p>
                </div>
              )}
            </div>

            {/* State Pension */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Target className="w-6 h-6 text-blue-600" />
                  <h3 className="text-xl font-bold text-gray-900">UK State Pension</h3>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeStatePension}
                    onChange={(e) => setIncludeStatePension(e.target.checked)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">Include</span>
                </label>
              </div>

              {includeStatePension && (
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    State Pension Age
                  </label>
                  <input
                    type="number"
                    value={statePensionAge}
                    onChange={(e) => setStatePensionAge(parseInt(e.target.value) || 67)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="65"
                    max="70"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Currently 66-67 depending on birth year. Check yours at gov.uk/state-pension-age
                  </p>
                </div>
              )}

              <div className="bg-white rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-700">Full State Pension (2024/25)</span>
                  <span className="font-bold text-blue-600">£{FULL_STATE_PENSION_ANNUAL.toLocaleString()}/year</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">Weekly amount</span>
                  <span className="text-sm font-semibold text-gray-900">£221.20</span>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Full State Pension requires 35 qualifying years of National Insurance contributions.
                  Check your forecast at gov.uk/check-state-pension
                </p>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-6">
            {/* Total Retirement Income */}
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-lg p-8 text-white">
              <div className="flex items-center space-x-3 mb-4">
                <Target className="w-6 h-6" />
                <h3 className="text-xl font-bold">Retirement Income</h3>
              </div>

              {/* If retiring before State Pension Age */}
              {includeStatePension && retirementAge < statePensionAge ? (
                <>
                  <div className="bg-white/10 rounded-lg p-4 mb-4">
                    <div className="text-sm text-blue-100 mb-2">Age {retirementAge} - {statePensionAge - 1} (Before State Pension)</div>
                    <div className="text-4xl font-bold mb-1">
                      £{results.monthlyIncomeBeforeStatePension.toLocaleString()}/mo
                    </div>
                    <p className="text-blue-100 text-sm">
                      £{results.annualIncomeBeforeStatePension.toLocaleString()}/year
                    </p>
                  </div>

                  <div className="bg-white/20 rounded-lg p-4 border-2 border-white/30">
                    <div className="text-sm text-blue-100 mb-2">Age {statePensionAge}+ (With State Pension)</div>
                    <div className="text-5xl font-bold mb-1">
                      £{results.monthlyIncome.toLocaleString()}/mo
                    </div>
                    <p className="text-blue-100 text-lg">
                      £{results.totalAnnualIncome.toLocaleString()}/year
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-5xl font-bold mb-2">
                    £{results.monthlyIncome.toLocaleString()}/mo
                  </div>
                  <p className="text-blue-100 text-lg">
                    £{results.totalAnnualIncome.toLocaleString()}/year in {results.yearsToRetirement} years
                  </p>
                  {includeStatePension && retirementAge === statePensionAge && (
                    <p className="text-blue-100 text-sm mt-2">
                      ✓ Includes State Pension from age {statePensionAge}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Income Breakdown */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Income Breakdown</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">SIPP / Personal Pension</span>
                  <span className="font-bold text-purple-600">£{Math.round((results.sippFutureValue * 0.75 * 0.04) / 12).toLocaleString()}/mo</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Workplace Pension</span>
                  <span className="font-bold text-green-600">£{Math.round((results.workplaceFutureValue * 0.75 * 0.04) / 12).toLocaleString()}/mo</span>
                </div>
                {hasDefinedBenefit && (
                  <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Defined Benefit Pension</span>
                    <span className="font-bold text-amber-600">£{Math.round(results.definedBenefitIncome / 12).toLocaleString()}/mo</span>
                  </div>
                )}
                {includeStatePension && (
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">State Pension</span>
                    <span className="font-bold text-blue-600">£{Math.round(results.statePensionIncome / 12).toLocaleString()}/mo</span>
                  </div>
                )}
              </div>
            </div>

            {/* Tax-Free Lump Sum */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-green-200">
              <div className="flex items-center space-x-3 mb-3">
                <Gift className="w-5 h-5 text-green-600" />
                <h3 className="text-lg font-bold text-gray-900">Tax-Free Lump Sum (25%)</h3>
              </div>
              <div className="text-3xl font-bold text-green-600 mb-1">
                £{results.taxFreeLumpSum.toLocaleString()}
              </div>
              <p className="text-sm text-gray-600">
                Tax-free cash from SIPP and Workplace pensions
              </p>
              {results.taxFreeLumpSum >= 268275 && (
                <p className="text-xs text-amber-600 mt-2 font-semibold">
                  ⓘ Capped at maximum Lump Sum Allowance (£268,275)
                </p>
              )}
            </div>

            {/* Pension Pot Values */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Pension Pot Values at {retirementAge}</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">SIPP / Personal</span>
                  <span className="font-semibold text-gray-900">£{results.sippFutureValue.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Workplace Pension</span>
                  <span className="font-semibold text-gray-900">£{results.workplaceFutureValue.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                  <span className="text-sm font-semibold text-gray-900">Total Pension Pots</span>
                  <span className="font-bold text-blue-600">£{results.totalDCPots.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Contribution Summary */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Contribution Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Your Contributions</span>
                  <span className="font-semibold text-gray-900">£{results.totalContributed.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Tax Relief (20%)</span>
                  <span className="font-semibold text-green-600">+£{results.totalTaxRelief.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Employer Contributions</span>
                  <span className="font-semibold text-green-600">+£{results.totalEmployerContributions.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                  <span className="text-sm font-semibold text-gray-900">Investment Growth</span>
                  <span className="font-semibold text-blue-600">
                    £{(results.totalDCPots - results.totalContributed - results.totalTaxRelief - results.totalEmployerContributions).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Educational Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Understanding Your Pension Types */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Understanding UK Pension Types</h2>

            <div className="space-y-4">
              <div>
                <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                  <Briefcase className="w-5 h-5 text-purple-600 mr-2" />
                  SIPP (Self-Invested Personal Pension)
                </h3>
                <p className="text-sm text-gray-700 mb-2">
                  A personal pension where you choose your own investments. Offers flexibility and control.
                </p>
                <ul className="text-xs text-gray-600 space-y-1 ml-4">
                  <li>• 20% tax relief automatically added</li>
                  <li>• Access from age 55 (rising to 57 in 2028)</li>
                  <li>• 25% tax-free lump sum available</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                  <Building2 className="w-5 h-5 text-green-600 mr-2" />
                  Workplace Pension
                </h3>
                <p className="text-sm text-gray-700 mb-2">
                  Auto-enrollment pension through your employer. Both you and employer contribute.
                </p>
                <ul className="text-xs text-gray-600 space-y-1 ml-4">
                  <li>• Employer contributions (minimum 3%)</li>
                  <li>• Tax relief on your contributions</li>
                  <li>• Same access rules as SIPP</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                  <Gift className="w-5 h-5 text-amber-600 mr-2" />
                  Defined Benefit Pension
                </h3>
                <p className="text-sm text-gray-700 mb-2">
                  Final salary or career average pension. Guarantees income for life.
                </p>
                <ul className="text-xs text-gray-600 space-y-1 ml-4">
                  <li>• Fixed annual income based on salary and years</li>
                  <li>• Usually includes inflation protection</li>
                  <li>• May offer spouse/partner benefits</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                  <Target className="w-5 h-5 text-blue-600 mr-2" />
                  State Pension
                </h3>
                <p className="text-sm text-gray-700 mb-2">
                  Government pension based on National Insurance contributions.
                </p>
                <ul className="text-xs text-gray-600 space-y-1 ml-4">
                  <li>• Requires 35 years for full amount</li>
                  <li>• Currently £11,502/year (£221.20/week)</li>
                  <li>• Payable from State Pension age (currently 67)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 2024/25 Contribution Limits */}
          <div className="bg-blue-50 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2024/25 Contribution Limits</h2>

            <div className="space-y-4">
              <div className="bg-white rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <PoundSterling className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Annual Allowance</h3>
                    <p className="text-sm text-gray-700">
                      £60,000 per year (including tax relief) or 100% of earnings, whichever is lower
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Tapered Allowance</h3>
                    <p className="text-sm text-gray-700">
                      If you earn over £260,000, your allowance reduces (minimum £10,000)
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Calendar className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Carry Forward</h3>
                    <p className="text-sm text-gray-700">
                      Use unused allowances from previous 3 tax years (if you were a member)
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Lifetime Allowance Abolished</h3>
                    <p className="text-sm text-gray-700">
                      From April 2024—no limit on total pension pot size
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Track with PlanLens */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 mb-12">
          <div className="text-center max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <PieChart className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Track All Your Pensions with PlanLens
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              This calculator gives you projections. PlanLens gives you the complete picture with
              portfolio tracking, AI insights, and comprehensive retirement planning.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-gray-900">Track All Pension Types</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-gray-900">AI-Powered Insights</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-gray-900">Comprehensive Planning</p>
              </div>
            </div>

            <button
              onClick={() => navigate('/')}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition shadow-lg hover:shadow-xl"
            >
              Start Tracking Your Pensions Free
            </button>
          </div>
        </div>

        {/* Important Disclaimer */}
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-gray-900 mb-2">Important Disclaimer</h3>
              <p className="text-sm text-gray-700 mb-2">
                This calculator provides estimates only and should not be considered financial advice.
                Actual returns will vary based on investment performance, fees, and market conditions.
              </p>
              <p className="text-sm text-gray-700">
                For personalized pension advice and planning, please consult an Independent Financial Adviser (IFA)
                regulated by the Financial Conduct Authority (FCA). Check your State Pension forecast at gov.uk/check-state-pension
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-400">
            © 2025 PlanLens. UK Pension Calculator for comprehensive retirement planning.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default UKPensionCalculatorPage;
